// src/components/ProjectManagement/ChangeProjectEndDateModal.jsx
import React, { useState } from 'react';
import { db } from '../../firebase/config';
import { doc, updateDoc, addDoc, collection, query, where, getDocs } from 'firebase/firestore';

const ChangeProjectEndDateModal = ({ project, onClose, onSuccess, currentUser }) => {
  const [newEndDate, setNewEndDate] = useState(
    project.endDate?.toDate?.()?.toISOString().split('T')[0] || 
    (project.endDate ? new Date(project.endDate).toISOString().split('T')[0] : '')
  );
  const [clientConfirmed, setClientConfirmed] = useState(false);
  const [reason, setReason] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async () => {
    if (!newEndDate) {
      alert('Please select a new end date');
      return;
    }
    if (!clientConfirmed) {
      alert('Please confirm that the client has agreed to the new end date');
      return;
    }
    if (!reason.trim()) {
      alert('Please provide a reason for changing the project end date');
      return;
    }

    setIsLoading(true);
    try {
      const newDate = new Date(newEndDate);
      
      await updateDoc(doc(db, 'projects', project.id), {
        endDate: newDate,
        previousEndDate: project.endDate,
        endDateChangedAt: new Date(),
        endDateChangedBy: currentUser.uid,
        endDateChangeReason: reason,
        clientConfirmed: true,
        updatedAt: new Date()
      });

      // Notify all workers assigned to tasks in this project
      const tasksQuery = query(collection(db, 'tasks'), where('projectId', '==', project.id));
      const tasksSnapshot = await getDocs(tasksQuery);
      const notifiedWorkers = new Set();
      
      for (const taskDoc of tasksSnapshot.docs) {
        const task = taskDoc.data();
        if (task.assignedTo && !notifiedWorkers.has(task.assignedTo)) {
          notifiedWorkers.add(task.assignedTo);
          await addDoc(collection(db, 'notifications'), {
            userId: task.assignedTo,
            type: 'project_end_date_changed',
            title: '📅 Project End Date Changed',
            message: `Project "${project.projectName}" end date has been changed to ${newDate.toLocaleDateString()}. Reason: ${reason}`,
            projectId: project.id,
            read: false,
            createdAt: new Date()
          });
        }
      }

      alert('✅ Project end date updated successfully!');
      onSuccess?.();
      onClose();
    } catch (error) {
      console.error('Error changing project end date:', error);
      alert('Failed to change project end date');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" style={{ maxWidth: '500px' }} onClick={(e) => e.stopPropagation()}>
        <h3>📅 Change Project End Date</h3>
        <p><strong>Project:</strong> {project.projectName}</p>
        <p><strong>Current End Date:</strong> {
          project.endDate?.toDate?.()?.toLocaleDateString() || 
          (project.endDate ? new Date(project.endDate).toLocaleDateString() : 'Not set')
        }</p>
        
        <label>New End Date *</label>
        <input 
          type="date" 
          value={newEndDate}
          onChange={(e) => setNewEndDate(e.target.value)}
          min={new Date().toISOString().split('T')[0]}
        />
        
        <label style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '16px' }}>
          <input 
            type="checkbox" 
            checked={clientConfirmed}
            onChange={(e) => setClientConfirmed(e.target.checked)}
          />
          I confirm that the client has agreed to this new end date
        </label>
        
        <label>Reason for Change *</label>
        <textarea 
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="e.g., Client requested extension, Material delivery delay, etc."
          rows="3"
          style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #ddd', marginTop: '8px' }}
        />
        
        <div className="modal-actions" style={{ marginTop: '20px' }}>
          <button onClick={handleSubmit} className="assign-btn" disabled={isLoading}>
            {isLoading ? 'Updating...' : '✓ Update Project End Date'}
          </button>
          <button onClick={onClose} className="cancel-btn">Cancel</button>
        </div>
      </div>
    </div>
  );
};

export default ChangeProjectEndDateModal;